import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Typography, CardContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import ResourcingCard from '~/modules/resource-management/ResourcingOverview/components/ResourcingCard';
import { useResourceAvailabilityPermissions } from '~/modules/resource-management/ResourcingOverview/hooks';
import {
  LUXON_SCALE,
  useResourceOverviewToolbarContext
} from '~/modules/resource-management/ResourceManagementDashboard/hooks/useResourceManagementDashboardContext';
import { compareDateObject } from '~/modules/common/dates/compare';
import { BenchOverAllocationTrackerDetailsSection } from './BenchOverAllocationDetailsSection';
import BenchOverAllocationTimeline from './BenchOverAllocationTimeline';

const useStyles = makeStyles(theme => ({
  title: {
    ...theme.typography.h6,
    color: theme.palette.text.secondary,
    lineHeight: `${theme.spacing(3)}px`,
    marginRight: theme.spacing(1)
  },
  cardContent: {
    padding: 0
  },
  selectPeriodMessage: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(1, 2),
    width: '100%'
  }
}));

export const BenchOverAllocationTrackerCard = () => {
  const {
    periodScale,
    setDate: setToolbarDate,
    dateRange
  } = useResourceOverviewToolbarContext();
  const { startDate: toolbarDate } = dateRange;

  const { formatMessage } = useIntl();
  const classes = useStyles();

  const [selectedPeriod, setSelectedPeriod] = useState(toolbarDate);

  const handlePeriodClick = useCallback(
    periodDate => {
      if (
        selectedPeriod &&
        compareDateObject(selectedPeriod, periodDate) === 0
      ) {
        setSelectedPeriod(null);
      } else {
        setSelectedPeriod(periodDate);
        setToolbarDate(periodDate);
      }
    },
    [setToolbarDate, selectedPeriod]
  );

  useEffect(() => {
    if (
      selectedPeriod &&
      compareDateObject(selectedPeriod, toolbarDate) !== 0
    ) {
      setSelectedPeriod(toolbarDate);
    }
  }, [toolbarDate, selectedPeriod]);

  const {
    isViewRoleEnabled,
    isViewCostCenterEnabled,
    isViewDivisionEnabled,
    isViewDepartmentEnabled,
    isViewEmployeeTypeEnabled,
    isViewLocationEnabled,
    isViewServiceCenterEnabled
  } = useResourceAvailabilityPermissions();

  const showBenchDetailSection =
    isViewRoleEnabled ||
    isViewCostCenterEnabled ||
    isViewDivisionEnabled ||
    isViewDepartmentEnabled ||
    isViewEmployeeTypeEnabled ||
    isViewLocationEnabled ||
    isViewServiceCenterEnabled;

  const selectPeriodMessage = useMemo(() => {
    const periodLabelKey = LUXON_SCALE[periodScale] || 'month';
    const periodLabel = formatMessage({
      id: `timePeriod.${periodLabelKey}`
    });

    return formatMessage(
      {
        id:
          'resourceManagementDashboard.benchOverAllocationTracker.selectPeriodMessage'
      },
      { period: periodLabel }
    );
  }, [periodScale, formatMessage]);

  const benchTrackerTitle = useMemo(
    () => (
      <Typography className={classes.title}>
        <FormattedMessage id="resourceManagementDashboard.benchOverAllocationTracker.title" />
      </Typography>
    ),
    [classes.title]
  );

  return (
    <ResourcingCard title={benchTrackerTitle}>
      <CardContent className={classes.cardContent}>
        <BenchOverAllocationTimeline
          selectedPeriod={selectedPeriod}
          onPeriodClick={handlePeriodClick}
        />

        {showBenchDetailSection &&
          (selectedPeriod ? (
            <div
              id="bench-detail-panel"
              role="group"
              aria-label={formatMessage({
                id:
                  'resourceManagementDashboard.benchOverAllocationTracker.trackerDetails'
              })}
            >
              <BenchOverAllocationTrackerDetailsSection />
            </div>
          ) : (
            <Typography className={classes.selectPeriodMessage}>
              {selectPeriodMessage}
            </Typography>
          ))}
      </CardContent>
    </ResourcingCard>
  );
};

export default BenchOverAllocationTrackerCard;
